import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, AlertCircle } from 'lucide-react';
import { AuthLayout } from '@/components/AuthLayout';
import { formatApiErrorDetail } from '@/utils/format-error';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout heroText="All your Substacks, blogs, podcasts, and Twitter feeds in one place.">
      <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight uppercase mb-2">Welcome Back</h2>
      <p className="text-zinc-600 font-medium mb-8">Sign in to your feed reader</p>

      {error && (
        <div data-testid="login-error" className="flex items-center gap-2 bg-red-100 border-2 border-black p-3 mb-6 text-sm font-medium text-red-700">
          <AlertCircle strokeWidth={2.5} size={16} />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-600 mb-2 block">Email</label>
          <div className="relative">
            <Mail strokeWidth={2.5} size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="brutalist-input pl-10"
              placeholder="your@email.com"
              required
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-600 mb-2 block">Password</label>
          <div className="relative">
            <Lock strokeWidth={2.5} size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="brutalist-input pl-10"
              placeholder="Enter password"
              required
            />
          </div>
        </div>
        <button
          data-testid="login-submit-button"
          type="submit"
          disabled={isLoading}
          className="brutalist-btn-primary w-full text-center disabled:opacity-60"
        >
          {isLoading ? 'Signing in...' : 'Sign In'}
        </button>
      </form>

      <p className="mt-6 text-center text-sm font-medium text-zinc-600">
        Don't have an account?{' '}
        <Link data-testid="go-to-register-link" to="/register" className="text-pink-500 font-bold uppercase hover:underline">
          Sign Up
        </Link>
      </p>
    </AuthLayout>
  );
}
